/**
 * Jornada laboral y horas extraordinarias — legislación chilena.
 *
 * ⚠️ Los parámetros legales son configurables y deben ser validados por el área
 * laboral de la empresa antes de usarse en nómina. La app calcula y alerta;
 * no reemplaza asesoría legal.
 *
 * Marco implementado (Código del Trabajo + Ley 21.561):
 *  - Jornada ordinaria semanal: 42 h desde el 26-04-2026.
 *    Escalera: 45 h → 44 h (26-04-2024) → 42 h (26-04-2026) → 40 h (26-04-2028).
 *  - Máximo 10 h ordinarias diarias (art. 28).
 *  - Máximo 2 h extraordinarias diarias (art. 31).
 *  - Recargo mínimo 50% sobre el sueldo convenido para la jornada ordinaria (art. 32).
 *  - Pacto de horas extras por escrito, hasta 3 meses, renovable (art. 32).
 *  - Colación mínimo 30 min, no imputable a la jornada (art. 34).
 *  - Minería opera con sistema excepcional de distribución de jornada
 *    autorizado por la Dirección del Trabajo (art. 38): 7x7, 4x3, 10x10.
 *  - Registro de asistencia obligatorio (art. 33 + DS 30/2023).
 */

// ── Parámetros ────────────────────────────────────────────────────────────

export type TipoJornada = 'ORDINARIA' | 'SISTEMA_EXCEPCIONAL';

export interface Jornada {
  id: string;
  nombre: string;
  tipo: TipoJornada;
  /** Horas ordinarias por semana. 42 vigente desde el 26-04-2026. */
  horasSemanales: number;
  /** Tope diario de jornada ordinaria (art. 28). */
  maxHorasDiarias: number;
  /** Tope de horas extraordinarias por día (art. 31). */
  maxHorasExtraDiarias: number;
  /** Días de trabajo por semana en régimen ordinario (entre 5 y 6). */
  diasTrabajoSemana: number;
  /** Sólo en sistema excepcional: duración del turno pactado. */
  horasTurno?: number;
  ciclosDiasTrabajo?: number;
  ciclosDiasDescanso?: number;
  minutosColacion: number;
  colacionImputable: boolean;
  /** Recargo de la hora extraordinaria, en porcentaje. Mínimo legal 50. */
  recargoExtraPct: number;
  /** Resolución de la Dirección del Trabajo que autoriza el sistema excepcional. */
  resolucionDt?: string;
}

/** Escalera de la Ley 21.561. Se elige por fecha. */
export const ESCALERA_LEY_21561: { desde: string; horas: number }[] = [
  { desde: '0000-01-01', horas: 45 },
  { desde: '2024-04-26', horas: 44 },
  { desde: '2026-04-26', horas: 42 },
  { desde: '2028-04-26', horas: 40 },
];

/** Horas semanales legales vigentes a una fecha dada (yyyy-MM-dd). */
export function horasSemanalesLegales(fecha: string): number {
  let horas = 45;
  for (const tramo of ESCALERA_LEY_21561) {
    if (fecha >= tramo.desde) horas = tramo.horas;
  }
  return horas;
}

export const JORNADA_ORDINARIA: Jornada = {
  id: 'ordinaria-42',
  nombre: 'Ordinaria 42 h',
  tipo: 'ORDINARIA',
  horasSemanales: 42,
  maxHorasDiarias: 10,
  maxHorasExtraDiarias: 2,
  diasTrabajoSemana: 5,
  minutosColacion: 30,
  colacionImputable: false,
  recargoExtraPct: 50,
};

export const JORNADA_7X7: Jornada = {
  id: 'excepcional-7x7',
  nombre: 'Sistema excepcional 7x7',
  tipo: 'SISTEMA_EXCEPCIONAL',
  horasSemanales: 42,
  maxHorasDiarias: 12,
  maxHorasExtraDiarias: 2,
  diasTrabajoSemana: 7,
  horasTurno: 10,
  ciclosDiasTrabajo: 7,
  ciclosDiasDescanso: 7,
  minutosColacion: 60,
  colacionImputable: false,
  recargoExtraPct: 50,
  resolucionDt: '',
};

// ── Marcas y resultado ────────────────────────────────────────────────────

export interface MarcaAsistencia {
  trabajadorId: string;
  fecha: string; // yyyy-MM-dd
  entrada: Date | null;
  salida: Date | null;
  inicioColacion?: Date | null;
  finColacion?: Date | null;
}

export interface JornadaDia {
  trabajadorId: string;
  fecha: string;
  minutosPresencia: number | null;
  minutosColacion: number;
  minutosTrabajados: number | null;
  minutosOrdinarios: number | null;
  minutosExtra: number | null;
  minutosNocturnos: number | null;
  esDomingo: boolean;
  esFestivo: boolean;
  /** Incumplimientos detectados, para el panel de alertas. */
  alertas: string[];
  /** true cuando falta una marca y no se puede calcular. */
  incompleta: boolean;
}

const MS_MIN = 60_000;
const NOCTURNO_DESDE = 21;
const NOCTURNO_HASTA = 6;

function minutos(a: Date | null | undefined, b: Date | null | undefined): number | null {
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / MS_MIN);
}

/** Minutos de la jornada que caen en horario nocturno (21:00–06:00). Informativo. */
function minutosNocturnos(entrada: Date, salida: Date): number {
  let total = 0;
  const cursor = new Date(entrada);
  cursor.setSeconds(0, 0);
  while (cursor < salida) {
    const h = cursor.getHours();
    if (h >= NOCTURNO_DESDE || h < NOCTURNO_HASTA) total++;
    cursor.setMinutes(cursor.getMinutes() + 1);
  }
  return total;
}

/** Tope diario de jornada ordinaria según el tipo de jornada. */
export function limiteOrdinarioDiarioMin(j: Jornada): number {
  if (j.tipo === 'SISTEMA_EXCEPCIONAL' && j.horasTurno) {
    return Math.min(j.horasTurno, j.maxHorasDiarias) * 60;
  }
  const porDia = j.horasSemanales / Math.max(1, j.diasTrabajoSemana);
  return Math.min(porDia, j.maxHorasDiarias) * 60;
}

export interface OpcionesDia {
  jornada: Jornada;
  esFestivo?: boolean;
  /** ¿Hay pacto de horas extras vigente a esta fecha? (art. 32) */
  conPactoVigente?: boolean;
}

/** Calcula la jornada de un día a partir de las marcas de asistencia. */
export function calcularJornadaDia(m: MarcaAsistencia, o: OpcionesDia): JornadaDia {
  const { jornada: j } = o;
  const alertas: string[] = [];

  const presencia = minutos(m.entrada, m.salida);
  const incompleta = presencia === null;

  if (m.entrada && !m.salida) alertas.push('Marca de entrada sin salida.');
  if (!m.entrada && m.salida) alertas.push('Marca de salida sin entrada.');
  if (presencia !== null && presencia < 0) alertas.push('La salida es anterior a la entrada.');

  // Colación: la registrada, o la mínima de la jornada si no se marcó.
  const colacionMarcada = minutos(m.inicioColacion, m.finColacion);
  const colacion = colacionMarcada ?? (presencia !== null ? j.minutosColacion : 0);
  if (colacionMarcada !== null && colacionMarcada < 30) {
    alertas.push(`Colación de ${colacionMarcada} min: el mínimo legal es 30.`);
  }

  const trabajados =
    presencia === null ? null : Math.max(0, presencia - (j.colacionImputable ? 0 : colacion));

  const limite = limiteOrdinarioDiarioMin(j);
  const ordinarios = trabajados === null ? null : Math.min(trabajados, limite);
  const extra = trabajados === null ? null : Math.max(0, trabajados - limite);

  if (trabajados !== null && trabajados > j.maxHorasDiarias * 60) {
    alertas.push(
      `Jornada de ${(trabajados / 60).toFixed(1)} h: supera el máximo de ${j.maxHorasDiarias} h diarias.`,
    );
  }
  if (extra !== null && extra > j.maxHorasExtraDiarias * 60) {
    alertas.push(
      `${(extra / 60).toFixed(1)} h extraordinarias: el máximo legal son ${j.maxHorasExtraDiarias} h al día.`,
    );
  }
  if (extra !== null && extra > 0 && o.conPactoVigente === false) {
    alertas.push('Horas extraordinarias sin pacto escrito vigente.');
  }

  const [a, me, d] = m.fecha.split('-').map(Number);
  const diaSemana = new Date(a!, me! - 1, d!).getDay();

  return {
    trabajadorId: m.trabajadorId,
    fecha: m.fecha,
    minutosPresencia: presencia,
    minutosColacion: colacion,
    minutosTrabajados: trabajados,
    minutosOrdinarios: ordinarios,
    minutosExtra: extra,
    minutosNocturnos: m.entrada && m.salida ? minutosNocturnos(m.entrada, m.salida) : null,
    esDomingo: diaSemana === 0,
    esFestivo: o.esFestivo ?? false,
    alertas,
    incompleta,
  };
}

// ── Consolidado semanal ───────────────────────────────────────────────────

export interface ResumenSemana {
  trabajadorId: string;
  /** Lunes de la semana, yyyy-MM-dd. */
  semana: string;
  minutosOrdinarios: number;
  minutosExtra: number;
  diasTrabajados: number;
  excedeJornadaSemanal: boolean;
  alertas: string[];
}

/** Lunes de la semana a la que pertenece una fecha. */
export function lunesDe(fecha: string): string {
  const [a, m, d] = fecha.split('-').map(Number);
  const dt = new Date(a!, m! - 1, d!);
  const desplazamiento = (dt.getDay() + 6) % 7; // lunes = 0
  dt.setDate(dt.getDate() - desplazamiento);
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

/**
 * Consolida por semana y reclasifica como extraordinario todo lo que exceda
 * la jornada ordinaria semanal pactada.
 */
export function consolidarSemanas(dias: JornadaDia[], j: Jornada): ResumenSemana[] {
  const porClave = new Map<string, JornadaDia[]>();
  for (const d of dias) {
    const clave = `${d.trabajadorId}|${lunesDe(d.fecha)}`;
    porClave.set(clave, [...(porClave.get(clave) ?? []), d]);
  }

  const topeSemanal = j.horasSemanales * 60;

  return [...porClave.entries()].map(([clave, ds]) => {
    const [trabajadorId, semana] = clave.split('|');
    const ordinariosBrutos = ds.reduce((s, d) => s + (d.minutosOrdinarios ?? 0), 0);
    const extraDiario = ds.reduce((s, d) => s + (d.minutosExtra ?? 0), 0);

    const excede = ordinariosBrutos > topeSemanal;
    const excedente = excede ? ordinariosBrutos - topeSemanal : 0;

    const alertas: string[] = [];
    if (excede) {
      alertas.push(
        `${(ordinariosBrutos / 60).toFixed(1)} h ordinarias en la semana: ` +
          `${(excedente / 60).toFixed(1)} h por sobre las ${j.horasSemanales} h pactadas ` +
          `se reclasifican como extraordinarias.`,
      );
    }
    const diasTrabajados = ds.filter((d) => (d.minutosTrabajados ?? 0) > 0).length;
    if (j.tipo === 'ORDINARIA' && diasTrabajados > 6) {
      alertas.push('Más de 6 días trabajados en la semana sin descanso semanal.');
    }

    return {
      trabajadorId: trabajadorId!,
      semana: semana!,
      minutosOrdinarios: Math.min(ordinariosBrutos, topeSemanal),
      minutosExtra: extraDiario + excedente,
      diasTrabajados,
      excedeJornadaSemanal: excede,
      alertas,
    };
  });
}

// ── Valorización ──────────────────────────────────────────────────────────

/**
 * Valor de la hora ordinaria.
 * Fórmula estándar chilena: (sueldo mensual × 7) / (30 × jornada semanal).
 */
export function valorHoraOrdinaria(sueldoBaseMensual: number, horasSemanales: number): number {
  if (horasSemanales <= 0) return 0;
  return (sueldoBaseMensual * 7) / (30 * horasSemanales);
}

/**
 * Valor de la hora extraordinaria, con su recargo.
 * Factores resultantes sobre el sueldo base mensual:
 *   45 h → 0,0077778 · 44 h → 0,0079545 · 42 h → 0,0083333 · 40 h → 0,0087500
 */
export function valorHoraExtra(
  sueldoBaseMensual: number,
  horasSemanales: number,
  recargoPct = 50,
): number {
  return valorHoraOrdinaria(sueldoBaseMensual, horasSemanales) * (1 + recargoPct / 100);
}

/** Monto a pagar por las horas extraordinarias de un período. */
export function montoHorasExtra(
  minutosExtra: number,
  sueldoBaseMensual: number,
  j: Jornada,
): number {
  const valorHora = valorHoraExtra(sueldoBaseMensual, j.horasSemanales, j.recargoExtraPct);
  return Math.round((minutosExtra / 60) * valorHora);
}

/** Formatea minutos como horas decimales: 570 → «9,5 h». */
export function horas(min: number | null | undefined, decimales = 1): string {
  if (min == null) return '—';
  return `${(min / 60).toLocaleString('es-CL', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  })} h`;
}
